import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Check,
  Close,
  Delete,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  CustomColumn,
  CustomColumnFormat,
  dependentsOf,
  deriveFieldId,
  detectCycle,
  evaluateColumn,
  formatCustomValue,
  formulaToString,
  parseFormula,
  validateAst,
  validateFieldName,
} from "../../../util/customColumns";

declare module "@mui/x-data-grid-premium" {
  interface ColumnsPanelPropsOverrides {
    customColumns: CustomColumn[];
    setCustomColumns: (
      next: CustomColumn[] | ((prev: CustomColumn[]) => CustomColumn[]),
    ) => void;
    displayNames: Record<string, string>;
    setDisplayNames: (
      next:
        | Record<string, string>
        | ((prev: Record<string, string>) => Record<string, string>),
    ) => void;
    columnVisibilityModel: Record<string, boolean>;
    setColumnVisibilityModel: (next: Record<string, boolean>) => void;
    baseFields: string[];
    allFields: string[];
    sampleRows: Record<string, any>[];
    apiRef: any;
    isMobile: boolean;
    resolveHeaderName: (field: string) => string;
    getDefaultHeaderName: (field: string) => string;
    dimensionColumns: string[];
    orderedFields: string[];
    setOrderedFields: (next: string[] | ((prev: string[]) => string[])) => void;
    // Parent-owned ref. Panel registers an "open editor" callback here on
    // mount; parent invokes it after showPreferences to expand the editor
    // when the panel is opened via the column menu's "Add custom column…" item.
    openCustomColumnEditorRef?: { current: (() => void) | null };
  }
}

const SOFT_CAP = 10;
const SOFT_CAP_WARN = 8;

type EditorState = {
  mode: "create" | "edit";
  fieldId?: string; // stable ID; preserved across edits to keep URL refs valid
  name: string; // display name (also drives the field ID for new columns)
  formula: string;
  decimals: "auto" | 0 | 1 | 2;
  percent: boolean;
  signed: boolean;
};

const emptyEditor = (): EditorState => ({
  mode: "create",
  name: "",
  formula: "",
  decimals: "auto",
  percent: false,
  signed: false,
});

interface PanelProps {
  customColumns: CustomColumn[];
  setCustomColumns: (
    next: CustomColumn[] | ((prev: CustomColumn[]) => CustomColumn[]),
  ) => void;
  displayNames: Record<string, string>;
  setDisplayNames: (
    next:
      | Record<string, string>
      | ((prev: Record<string, string>) => Record<string, string>),
  ) => void;
  columnVisibilityModel: Record<string, boolean>;
  setColumnVisibilityModel: (next: Record<string, boolean>) => void;
  baseFields?: string[];
  allFields?: string[];
  sampleRows?: Record<string, any>[];
  apiRef?: any;
  isMobile?: boolean;
  resolveHeaderName?: (field: string) => string;
  getDefaultHeaderName?: (field: string) => string;
  dimensionColumns?: string[];
  orderedFields?: string[];
  setOrderedFields?: (next: string[] | ((prev: string[]) => string[])) => void;
  // Parent-owned ref. Panel registers an "open editor" callback here on
  // mount; parent invokes it after showPreferences to auto-expand the editor
  // when the panel is opened via the column menu's "Add custom column…" item.
  openCustomColumnEditorRef?: { current: (() => void) | null };
}

export default function CustomColumnsPanel(props: PanelProps) {
  const {
    customColumns,
    setCustomColumns,
    displayNames,
    setDisplayNames,
    columnVisibilityModel,
    setColumnVisibilityModel,
    baseFields = [],
    allFields = [],
    sampleRows = [],
    apiRef,
    isMobile = false,
    resolveHeaderName,
    getDefaultHeaderName,
    dimensionColumns = [],
    orderedFields = [],
    setOrderedFields,
    openCustomColumnEditorRef,
  } = props;

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [renameField, setRenameField] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>("");

  const baseFieldSet = useMemo(() => new Set(baseFields), [baseFields]);
  const customFieldSet = useMemo(
    () => new Set(customColumns.map((c) => c.field)),
    [customColumns],
  );
  const dimensionSet = useMemo(
    () => new Set(dimensionColumns),
    [dimensionColumns],
  );
  // Fields available to reference inside a formula:
  //   - all base (non-dimension) numeric columns
  //   - other custom columns, MINUS the column currently being edited and
  //     anything that already (transitively) depends on it (would cycle)
  const pickerFields = useMemo(() => {
    const base = baseFields.filter((f) => !dimensionSet.has(f));
    const editingField =
      editor && editor.mode === "edit" ? editor.fieldId : null;
    const forbidden = new Set<string>();
    if (editingField) {
      forbidden.add(editingField);
      dependentsOf(editingField, customColumns).forEach((f) =>
        forbidden.add(f),
      );
    }
    const customRefs = customColumns
      .map((c) => c.field)
      .filter((f) => !forbidden.has(f));
    return [...base, ...customRefs];
  }, [baseFields, dimensionSet, editor, customColumns]);

  // ---- editor ----------------------------------------------------------

  const startCreate = () => {
    if (customColumns.length >= SOFT_CAP) return;
    setEditor(emptyEditor());
  };

  const startEdit = (cc: CustomColumn) => {
    setEditor({
      mode: "edit",
      fieldId: cc.field,
      name: cc.headerName ?? cc.field,
      formula: formulaToString(cc.expr),
      decimals: cc.format?.decimals ?? "auto",
      percent: cc.format?.percent ?? false,
      signed: cc.format?.signed ?? false,
    });
  };

  const cancelEditor = () => setEditor(null);

  // Register an "open editor" callback in the parent-owned ref so that the
  // column menu's "Add custom column…" item can imperatively trigger the
  // editor after opening the panel. We register on mount and clear on unmount;
  // the parent invokes via setTimeout(0) so the panel has time to mount.
  useEffect(() => {
    if (!openCustomColumnEditorRef) return;
    openCustomColumnEditorRef.current = () => {
      if (customColumns.length < SOFT_CAP) {
        setEditor(emptyEditor());
      }
    };
    return () => {
      if (openCustomColumnEditorRef.current) {
        openCustomColumnEditorRef.current = null;
      }
    };
  }, [openCustomColumnEditorRef, customColumns.length]);

  // Field ID computed live from the name (or pinned to the original on edit)
  const previewFieldId = useMemo(() => {
    if (!editor) return "";
    if (editor.mode === "edit" && editor.fieldId) return editor.fieldId;
    const taken = new Set<string>();
    baseFieldSet.forEach((f) => taken.add(f));
    customFieldSet.forEach((f) => taken.add(f));
    taken.add("Rank");
    taken.add("actions");
    return deriveFieldId(editor.name, taken);
  }, [editor, baseFieldSet, customFieldSet]);

  const validation = useMemo(() => {
    if (!editor) return { nameErr: null, formulaErr: null, ast: null };
    const customFieldsExcludingSelf = new Set(customFieldSet);
    if (editor.mode === "edit" && editor.fieldId) {
      customFieldsExcludingSelf.delete(editor.fieldId);
    }
    let nameErr: string | null = null;
    if (!editor.name.trim()) {
      nameErr = "Name is required";
    } else {
      // Validate the derived field ID — this catches reserved/duplicate cases
      // even though the user only sees the display name.
      nameErr = validateFieldName(
        previewFieldId,
        baseFieldSet,
        customFieldsExcludingSelf,
      );
    }
    if (!editor.formula.trim()) {
      return { nameErr, formulaErr: "Formula is required", ast: null };
    }
    const parsed = parseFormula(editor.formula);
    if ("error" in parsed) {
      return { nameErr, formulaErr: parsed.error, ast: null };
    }
    const validateErr = validateAst(parsed.ast, {
      baseFields: baseFieldSet,
      customFields: customFieldsExcludingSelf,
    });
    if (validateErr) {
      return { nameErr, formulaErr: validateErr, ast: null };
    }
    // Cycle check: would saving this column close a loop?
    const cycleErr = detectCycle({
      proposedField: previewFieldId,
      proposedExpr: parsed.ast,
      existing: customColumns,
      excludeOriginal: editor.mode === "edit" ? editor.fieldId : undefined,
    });
    if (cycleErr) {
      return { nameErr, formulaErr: cycleErr, ast: null };
    }
    return { nameErr, formulaErr: null, ast: parsed.ast };
  }, [editor, previewFieldId, baseFieldSet, customFieldSet, customColumns]);

  const previewValues = useMemo(() => {
    if (!editor || !validation.ast) return [];
    const format: CustomColumnFormat = {
      decimals: editor.decimals === "auto" ? undefined : editor.decimals,
      percent: editor.percent,
      signed: editor.signed,
    };
    const cc: CustomColumn = {
      field: previewFieldId || "preview",
      expr: validation.ast,
      format,
    };
    return sampleRows.map((row) => {
      const raw = evaluateColumn(cc, row);
      return formatCustomValue(raw, format);
    });
  }, [editor, validation.ast, sampleRows, previewFieldId]);

  const saveEditor = () => {
    if (!editor || !validation.ast) return;
    if (validation.nameErr || validation.formulaErr) return;
    const format: CustomColumnFormat = {};
    if (editor.decimals !== "auto") format.decimals = editor.decimals;
    if (editor.percent) format.percent = true;
    if (editor.signed) format.signed = true;
    const trimmedName = editor.name.trim();
    const cc: CustomColumn = {
      field: previewFieldId,
      expr: validation.ast,
      ...(trimmedName && trimmedName !== previewFieldId
        ? { headerName: trimmedName }
        : {}),
      ...(Object.keys(format).length > 0 ? { format } : {}),
    };
    setCustomColumns((prev) => {
      if (editor.mode === "edit" && editor.fieldId) {
        return prev.map((p) => (p.field === editor.fieldId ? cc : p));
      }
      return [...prev, cc];
    });
    setEditor(null);
  };

  const deleteCustomColumn = (field: string) => {
    setCustomColumns((prev) => prev.filter((p) => p.field !== field));
    setDisplayNames((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // ---- column reorder via panel ----------------------------------------

  // Source-of-truth ordered list of fields shown in the panel.
  // Priority:
  //   1. orderedFields (parent-controlled, captured by onColumnOrderChange and
  //      updated directly by panel arrow buttons) — survives re-renders
  //   2. apiRef snapshot — covers the pre-first-reorder case where
  //      orderedFields is still empty but the grid has its initial order
  //   3. allFields — initial render before apiRef populates
  //
  // Read on every render (no useMemo) so the snapshot is always fresh after a
  // reorder; useMemo with apiRef dep wouldn't re-run because apiRef itself
  // doesn't change reference.
  const stripFixed = (arr: string[]) =>
    arr.filter((f) => f !== "Rank" && f !== "actions");

  const orderedListedFields: string[] = (() => {
    if (orderedFields.length > 0) {
      const filtered = stripFixed(orderedFields);
      // Belt-and-suspenders: ensure every current field is represented (in case
      // a custom column was added after orderedFields was captured).
      const seen = new Set(filtered);
      const allKnown = new Set([
        ...baseFields,
        ...customColumns.map((c) => c.field),
      ]);
      const missing: string[] = [];
      allKnown.forEach((f) => {
        if (!seen.has(f) && f !== "Rank" && f !== "actions") missing.push(f);
      });
      return [...filtered.filter((f) => allKnown.has(f)), ...missing];
    }
    if (apiRef?.current?.getAllColumns) {
      try {
        return stripFixed(
          apiRef.current.getAllColumns().map((c: any) => c.field),
        );
      } catch {
        /* fall through */
      }
    }
    return stripFixed(allFields);
  })();

  const moveColumn = (field: string, dir: -1 | 1) => {
    // Drive ordering through setOrderedFields rather than apiRef.setColumnIndex.
    // The grid reads orderedFields back on its next render to apply the order;
    // this keeps the panel and the grid in lockstep without relying on
    // apiRef-internal state being in sync at the moment we read it.
    if (!setOrderedFields) return;
    setOrderedFields((prev) => {
      // Build a complete current order: prev (if populated) else the panel's
      // current view, with Rank/actions pinned in their canonical positions.
      const movable =
        prev.length > 0 ? stripFixed(prev) : orderedListedFields.slice();
      const idx = movable.indexOf(field);
      if (idx < 0) return prev;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= movable.length) return prev;
      const next = movable.slice();
      const [moved] = next.splice(idx, 1);
      next.splice(newIdx, 0, moved);
      // Reassemble with Rank pinned left and actions pinned right
      const hasActions =
        prev.includes("actions") || allFields.includes("actions");
      return ["Rank", ...next, ...(hasActions ? ["actions"] : [])];
    });
  };

  // ---- rename ----------------------------------------------------------
  //
  // Rename UX:
  //   - Click pencil → input opens, pre-filled with the current rename if one
  //     exists, else empty. Placeholder always shows the auto-derived default
  //     (so the reset path is "clear field, then save").
  //   - ✓ commits (empty trimmed value removes the rename, restoring default).
  //   - X (or Esc) cancels.
  //   - Blur is a no-op so accidental clicks elsewhere don't lose the edit.

  const startRename = (field: string) => {
    setRenameField(field);
    setRenameValue(displayNames[field] ?? "");
  };

  const commitRename = () => {
    if (!renameField) return;
    const trimmed = renameValue.trim();
    setDisplayNames((prev) => {
      const next = { ...prev };
      if (!trimmed) {
        delete next[renameField];
      } else {
        next[renameField] = trimmed;
      }
      return next;
    });
    setRenameField(null);
    setRenameValue("");
  };

  const cancelRename = () => {
    setRenameField(null);
    setRenameValue("");
  };

  const toggleVisibility = (field: string) => {
    const isVisible = columnVisibilityModel[field] !== false;
    setColumnVisibilityModel({
      ...columnVisibilityModel,
      [field]: !isVisible,
    });
  };

  // ---- render ----------------------------------------------------------

  const panelWidth = isMobile ? "calc(100vw - 32px)" : 380;

  return (
    <Box
      sx={{
        width: panelWidth,
        maxHeight: "70vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Custom Columns section ── */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "uppercase",
              color: "rgba(0,0,0,0.6)",
              letterSpacing: "0.3px",
            }}
          >
            Custom Columns
          </Typography>
          <Tooltip
            title={
              customColumns.length >= SOFT_CAP
                ? `Limit of ${SOFT_CAP} custom columns reached`
                : ""
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={startCreate}
                disabled={!!editor || customColumns.length >= SOFT_CAP}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  py: 0.25,
                }}
              >
                Custom Column
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {customColumns.length >= SOFT_CAP_WARN &&
          customColumns.length < SOFT_CAP && (
            <Typography
              sx={{ fontSize: "0.7rem", color: "warning.main", mt: 0.5 }}
            >
              Approaching limit ({customColumns.length}/{SOFT_CAP})
            </Typography>
          )}

        {customColumns.length > 0 && !editor && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {customColumns.map((cc) => (
              <Box
                key={cc.field}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 1,
                  py: 0.5,
                  border: "1px solid #e0e0e0",
                  borderRadius: "3px",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {cc.headerName || cc.field}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "rgba(0,0,0,0.6)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontFamily: "monospace",
                    }}
                  >
                    {formulaToString(cc.expr)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => startEdit(cc)}
                  aria-label="Edit"
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => deleteCustomColumn(cc.field)}
                  aria-label="Delete"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}

        {editor && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              border: "1px solid #4c4c4c",
              borderRadius: "3px",
              backgroundColor: "#fafafa",
            }}
          >
            <Stack spacing={1.5}>
              <TextField
                label="Name"
                size="small"
                fullWidth
                autoFocus
                value={editor.name}
                onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                error={!!validation.nameErr}
                helperText={
                  validation.nameErr ??
                  (previewFieldId
                    ? `Column ID: ${previewFieldId}`
                    : "Used as the column header")
                }
                inputProps={{ style: { fontSize: "0.85rem" } }}
              />

              <FormulaInput
                value={editor.formula}
                onChange={(v) => setEditor({ ...editor, formula: v })}
                fields={pickerFields}
                resolveHeaderName={resolveHeaderName}
                error={!!validation.formulaErr}
                helperText={
                  validation.formulaErr ??
                  // eslint-disable-next-line no-template-curly-in-string
                  "Type ${ to reference a column. Use + - * / and ()."
                }
              />

              {/* Column format subsection */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "rgba(0,0,0,0.6)",
                    letterSpacing: "0.3px",
                    mb: 0.75,
                  }}
                >
                  Column format
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <TextField
                    select
                    label="Decimals"
                    size="small"
                    value={editor.decimals}
                    onChange={(e) =>
                      setEditor({
                        ...editor,
                        decimals: (e.target.value === "auto"
                          ? "auto"
                          : Number(e.target.value)) as EditorState["decimals"],
                      })
                    }
                    sx={{ width: 100 }}
                    SelectProps={{ sx: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  >
                    <MenuItem value="auto">Auto</MenuItem>
                    <MenuItem value={0}>0</MenuItem>
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                  </TextField>
                  <Stack direction="column" spacing={0}>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={editor.percent}
                          onChange={(e) =>
                            setEditor({
                              ...editor,
                              percent: e.target.checked,
                            })
                          }
                          sx={{ p: 0.25 }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "0.8rem" }}>
                          Display as %
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={editor.signed}
                          onChange={(e) =>
                            setEditor({
                              ...editor,
                              signed: e.target.checked,
                            })
                          }
                          sx={{ p: 0.25 }}
                        />
                      }
                      label={
                        <Tooltip title="Show + on positive values (useful for diffs / variance)">
                          <Typography sx={{ fontSize: "0.8rem" }}>
                            Show +/− sign
                          </Typography>
                        </Tooltip>
                      }
                    />
                  </Stack>
                </Stack>
              </Box>

              {previewValues.length > 0 && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "rgba(0,0,0,0.6)",
                      letterSpacing: "0.3px",
                      mb: 0.5,
                    }}
                  >
                    Preview
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {previewValues.map((v, i) => (
                      <Chip
                        key={i}
                        label={v}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.75rem",
                          borderRadius: "3px",
                          backgroundColor: "#e0e0e0",
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                sx={{ pt: 0.5 }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={cancelEditor}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    color: "#4c4c4c",
                    borderColor: "#bdbdbd",
                    "&:hover": {
                      borderColor: "#4c4c4c",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={saveEditor}
                  disabled={
                    !!validation.nameErr ||
                    !!validation.formulaErr ||
                    !validation.ast
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    color: "#fff",
                    backgroundColor: "#4c4c4c",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#333",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.6)",
                      backgroundColor: "#9e9e9e",
                    },
                  }}
                >
                  {editor.mode === "edit" ? "Save changes" : "Add column"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>

      <Divider />

      {/* ── Visibility / rename / reorder section ── */}
      <Box sx={{ px: 2, pt: 1, pb: 2 }}>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "uppercase",
            color: "rgba(0,0,0,0.6)",
            letterSpacing: "0.3px",
            mb: 1,
          }}
        >
          Columns
        </Typography>
        <Stack spacing={0.25}>
          {orderedListedFields.map((field: string, idx: number) => {
            if (field === "Rank" || field === "actions") return null;
            const isVisible = columnVisibilityModel[field] !== false;
            const isCustom = customFieldSet.has(field);
            const isDimension = dimensionSet.has(field);
            const headerLabel = resolveHeaderName
              ? resolveHeaderName(field)
              : field;
            return (
              <Box
                key={field}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 0.25,
                  pr: 0.5,
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <Checkbox
                  size="small"
                  checked={isVisible}
                  onChange={() => toggleVisibility(field)}
                  sx={{ p: 0.5 }}
                />
                {renameField === field ? (
                  <TextField
                    size="small"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename();
                      else if (e.key === "Escape") cancelRename();
                    }}
                    autoFocus
                    placeholder={
                      getDefaultHeaderName ? getDefaultHeaderName(field) : field
                    }
                    sx={{ flex: 1, mx: 0.5 }}
                    inputProps={{ style: { fontSize: "0.8rem", padding: 4 } }}
                  />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {headerLabel}
                    </Typography>
                    {isCustom && (
                      <Chip
                        label="custom"
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: "0.6rem",
                          borderRadius: "3px",
                          backgroundColor: "#4c4c4c",
                          color: "#fff",
                        }}
                      />
                    )}
                    {isDimension && (
                      <Chip
                        label="dim"
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: "0.6rem",
                          borderRadius: "3px",
                          backgroundColor: "#e0e0e0",
                        }}
                      />
                    )}
                  </Box>
                )}
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => moveColumn(field, -1)}
                      disabled={idx === 0}
                      sx={{ p: 0.25 }}
                    >
                      <KeyboardArrowUp fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => moveColumn(field, 1)}
                      disabled={idx === orderedListedFields.length - 1}
                      sx={{ p: 0.25 }}
                    >
                      <KeyboardArrowDown fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {renameField === field ? (
                  <>
                    <Tooltip
                      title={renameValue.trim() ? "Save" : "Reset to default"}
                    >
                      <IconButton
                        size="small"
                        // mousedown over click so the button activates before
                        // the input loses focus
                        onMouseDown={(e) => {
                          e.preventDefault();
                          commitRename();
                        }}
                        sx={{ p: 0.25 }}
                      >
                        <Check fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          cancelRename();
                        }}
                        sx={{ p: 0.25 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title={displayNames[field] ? "Edit name" : "Rename"}>
                    <IconButton
                      size="small"
                      onClick={() => startRename(field)}
                      sx={{
                        p: 0.25,
                        ...(displayNames[field] ? { color: "#4c4c4c" } : {}),
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {isCustom && (
                  <Tooltip title="Delete custom column">
                    <IconButton
                      size="small"
                      onClick={() => deleteCustomColumn(field)}
                      sx={{ p: 0.25 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// FormulaInput — TextField with inline ${ autocomplete
// ---------------------------------------------------------------------------

function FormulaInput({
  value,
  onChange,
  fields,
  resolveHeaderName,
  error,
  helperText,
}: {
  value: string;
  onChange: (v: string) => void;
  fields: string[];
  resolveHeaderName?: (field: string) => string;
  error?: boolean;
  helperText?: string;
}) {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [cursorPos, setCursorPos] = useState(0);
  const [highlightIdx, setHighlightIdx] = useState(0);

  // Detect open ${...} reference at the cursor.
  // Returns null if not currently authoring a ref, else the partial token text.
  const refContext = useMemo(() => {
    const upToCursor = value.slice(0, cursorPos);
    const lastDollar = upToCursor.lastIndexOf("${");
    if (lastDollar === -1) return null;
    const between = upToCursor.slice(lastDollar + 2);
    if (between.includes("}")) return null;
    if (between.includes("\n")) return null; // don't span lines
    return { start: lastDollar, partial: between };
  }, [value, cursorPos]);

  // Each pickable field carries its current display label and a hint to show
  // alongside it when the label differs from the underlying field ID.
  // Filtering matches against either, so users can search by what they see in
  // headers or by the raw field name.
  const candidates = useMemo(() => {
    return fields.map((f) => {
      const label = resolveHeaderName ? resolveHeaderName(f) : f;
      return { field: f, label, hasAlias: label !== f };
    });
  }, [fields, resolveHeaderName]);

  const filtered = useMemo(() => {
    if (!refContext) return candidates.slice(0, 50);
    const q = refContext.partial.toLowerCase();
    if (!q) return candidates.slice(0, 50);
    return candidates
      .filter(
        (c) =>
          c.label.toLowerCase().includes(q) ||
          c.field.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [refContext, candidates]);

  useEffect(() => {
    setHighlightIdx(0);
  }, [refContext?.partial]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    setCursorPos(e.target.selectionStart ?? e.target.value.length);
  };

  // The textarea element fires these via `inputProps`; the runtime target is
  // always HTMLTextAreaElement here (multiline=true), but the prop type is the
  // union HTMLInputElement | HTMLTextAreaElement so we accept the wider type.
  const handleSelectionChange = (
    e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const target = e.currentTarget as HTMLTextAreaElement;
    setCursorPos(target.selectionStart ?? 0);
  };

  const insertField = (fieldName: string) => {
    if (!refContext) return;
    const { start, partial } = refContext;
    const before = value.slice(0, start);
    const afterCursor = value.slice(cursorPos);
    // Eat any partial closing "}" that may already be there
    const trailing = afterCursor.startsWith("}")
      ? afterCursor.slice(1)
      : afterCursor;
    // Eat any leftover partial token characters past the cursor (rare)
    const insertion = `\${${fieldName}}`;
    const next = before + insertion + trailing;
    const newCursor = before.length + insertion.length;
    onChange(next);
    // Move caret after the closing }
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(newCursor, newCursor);
        setCursorPos(newCursor);
      }
    });
    // Suppress unused-var lint: `partial` is captured for future logic.
    void partial;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!refContext || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertField(filtered[highlightIdx].field);
    } else if (e.key === "Escape") {
      e.preventDefault();
      // Move cursor past the partial without inserting — close the popper
      const el = inputRef.current;
      if (el) {
        const newCursor = value.length;
        el.setSelectionRange(newCursor, newCursor);
        setCursorPos(newCursor);
      }
    }
  };

  const open = !!refContext && filtered.length > 0;

  return (
    <Box ref={wrapperRef} sx={{ position: "relative" }}>
      <TextField
        label="Formula"
        size="small"
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        value={value}
        inputRef={(el: HTMLTextAreaElement) => {
          inputRef.current = el;
        }}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        error={error}
        helperText={helperText}
        inputProps={{
          style: {
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Code", monospace',
            fontSize: "0.8rem",
            lineHeight: 1.5,
          },
          spellCheck: false,
          autoCorrect: "off",
          autoCapitalize: "off",
          onSelect: handleSelectionChange,
          onKeyUp: handleSelectionChange,
          onClick: handleSelectionChange,
        }}
      />
      <Popper
        open={open}
        anchorEl={wrapperRef.current}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        sx={{ zIndex: 1500 }}
      >
        <Box
          sx={{
            backgroundColor: "#fff",
            border: "1px solid #4c4c4c",
            borderRadius: "3px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            maxHeight: 200,
            overflowY: "auto",
            minWidth: 220,
          }}
        >
          {filtered.map((c, i) => (
            <Box
              key={c.field}
              onMouseDown={(e) => {
                // mousedown beats blur — keeps focus in the textarea
                e.preventDefault();
                insertField(c.field);
              }}
              onMouseEnter={() => setHighlightIdx(i)}
              sx={{
                px: 1,
                py: 0.5,
                cursor: "pointer",
                backgroundColor: i === highlightIdx ? "#4c4c4c" : "transparent",
                color: i === highlightIdx ? "#fff" : "inherit",
                display: "flex",
                alignItems: "baseline",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  fontSize: "0.8rem",
                  fontFamily: c.hasAlias ? "inherit" : "monospace",
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {c.label}
              </Box>
              {c.hasAlias && (
                <Box
                  sx={{
                    fontSize: "0.7rem",
                    fontFamily: "monospace",
                    opacity: 0.65,
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.field}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Popper>
    </Box>
  );
}
